/**
 * GMA-1620 (CompassCare Web 5/14 — Clients) — page composition.
 *
 * Ties the Clients management pieces together: the tab set (All / Group Homes /
 * Independent Living), a severity status filter + name search, the group-home
 * rollup cards (Group Homes tab), the risk-sorted ClientsTable, and a shared
 * DetailDrawer. Filtering is delegated to the pure clientsModel so this stays
 * presentational; selecting a client opens the detail drawer with its sub-tabbed
 * body (ClientDetailBody: Overview/Environment/Devices/History/Notes; a Vitals
 * tab is a later slice).
 *
 * Presentational + fail-safe: `clients` is injected by the route (testable) and
 * defaults to []; malformed rows can't break the render. Colors/fonts/severity
 * come from the single design-token set.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import ClientsTable from "../../components/web/ClientsTable";
import GroupHomeCards from "../../components/web/GroupHomeCards";
import DetailDrawer from "../../components/web/DetailDrawer";
import ClientDetailBody from "../../components/web/ClientDetailBody";
import { CLIENT_TABS, filterClientsByTab } from "./clientsModel";
import { theme } from "../../styles/designTokens";

// Severity status chips (orthogonal to the tabs). `all` is the default.
const STATUS_CHIPS = Object.freeze([
  { key: "all", label: "All" },
  { key: "critical", label: "Critical" },
  { key: "atRisk", label: "At risk" },
  { key: "caution", label: "Caution" },
  { key: "normal", label: "Normal" },
]);

function chipStyle(active) {
  return {
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 13,
    fontFamily: theme.fontUI,
    cursor: "pointer",
    border: `1px solid ${active ? theme.textPrimary : theme.border}`,
    background: active ? theme.surface : "transparent",
    color: active ? theme.textPrimary : theme.textMuted,
  };
}

export default function ClientsPage({ clients = [], now }) {
  const [tab, setTab] = useState("all");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  // `undefined` = no home selected; a real homeId (which may itself be null for
  // an "Unknown home" rollup) is a valid selection.
  const [homeSel, setHomeSel] = useState(undefined);
  const [detail, setDetail] = useState(null); // selected client | null

  let list = filterClientsByTab(clients, tab);
  if (tab === "homes" && homeSel !== undefined) {
    list = list.filter((c) => c && c.homeId === homeSel);
  }
  if (status !== "all") {
    list = list.filter((c) => c && c.status === status);
  }
  const q = query.trim().toLowerCase();
  if (q) {
    list = list.filter((c) => c && `${c.name || ""} ${c.home || ""}`.toLowerCase().includes(q));
  }

  return (
    <div
      data-testid="clients-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <div
        role="group"
        aria-label="Client views"
        data-testid="clients-tabs"
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {CLIENT_TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`clients-tab-${t.key}`}
              aria-pressed={active}
              onClick={() => {
                setTab(t.key);
                setHomeSel(undefined);
              }}
              style={chipStyle(active)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        role="group"
        aria-label="Filter by status"
        data-testid="clients-filter-bar"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
      >
        {STATUS_CHIPS.map((s) => {
          const active = s.key === status;
          return (
            <button
              key={s.key}
              type="button"
              data-testid={`clients-status-${s.key}`}
              aria-pressed={active}
              onClick={() => setStatus(s.key)}
              style={chipStyle(active)}
            >
              {s.label}
            </button>
          );
        })}
        <input
          type="search"
          aria-label="Search clients by name or home"
          placeholder="Search clients…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            marginLeft: "auto",
            minWidth: 200,
            padding: "6px 10px",
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            background: theme.surface,
            color: theme.textPrimary,
            fontFamily: theme.fontUI,
            fontSize: 13,
          }}
        />
      </div>

      {tab === "homes" && (
        <GroupHomeCards
          clients={clients}
          selectedHomeId={homeSel}
          onSelectHome={(id) => setHomeSel((prev) => (prev === id ? undefined : id))}
        />
      )}

      <ClientsTable
        clients={list}
        now={now}
        selectedId={detail ? detail.id : null}
        onSelectClient={(c) => setDetail(c)}
      />

      <DetailDrawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? detail.name || "Client detail" : ""}
        type="client"
      >
        <ClientDetailBody client={detail} />
      </DetailDrawer>
    </div>
  );
}

ClientsPage.propTypes = {
  clients: PropTypes.array,
  now: PropTypes.number,
};
